import { Component, OnInit, AfterViewInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation, ElementRef, ViewChild, inject } from '@angular/core';

import { Client } from 'src/providers/client/client';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

@Component({
  selector: 'onboarding-call',
  templateUrl: './onboarding-call.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class OnboardingCallComponent implements OnInit, AfterViewInit, OnDestroy {
  private rightSidePanelService = inject(RightSidePanelService);
  private cdr = inject(ChangeDetectorRef);
  private storage = inject(TidyStorage);
  private client = inject(Client);
  windowService = inject(WindowService);

  fromGettingStarted = false;
  addressId: number | null = null;
  callScheduled = false;

  @ViewChild('calendlyContainer') calendlyContainer: ElementRef;

  private calendlyScript: HTMLScriptElement | null = null;
  private observer: MutationObserver | null = null;
  private boundCalendlyListener = this.onCalendlyMessage.bind(this);

  async ngOnInit() {
    const params = await this.storage.retrieve('dialog-params');
    this.fromGettingStarted = !!params?.fromGettingStarted;
    this.addressId = params?.addressId || null;
    this.rightSidePanelService.setDialogPageTitle('Free Setup Call');
    await this.loadExistingSchedule();
    this.cdr.markForCheck();
  }

  ngAfterViewInit() {
    if (!this.callScheduled) {
      this.loadCalendlyWidget();
    }
  }

  private loadCalendlyWidget() {
    this.calendlyScript = document.createElement('script');
    this.calendlyScript.src = 'https://assets.calendly.com/assets/external/widget.js';
    this.calendlyScript.async = true;
    document.head.appendChild(this.calendlyScript);
    this.watchForIframe();
    window.addEventListener('message', this.boundCalendlyListener);
  }

  private async loadExistingSchedule() {
    if (!this.addressId) { return; }
    try {
      const key = `external.onboarding_step.${this.addressId}.concierge_call_scheduled`;
      const metadata: any[] = await this.client.getMetadata([key]);
      if (Array.isArray(metadata)) {
        const item = metadata.find(m => m?.key === key);
        if (item?.value) {
          this.callScheduled = true;
        }
      }
    } catch {
      // Non-fatal
    }
  }

  private onCalendlyMessage(event: MessageEvent) {
    if (!event.data?.event) { return; }
    if (event.data.event === 'calendly.event_scheduled') {
      this.onCallScheduled();
    }
  }

  private async onCallScheduled() {
    this.callScheduled = true;
    this.cdr.markForCheck();

    if (this.addressId) {
      try {
        await this.client.addMetadata({
          key: `onboarding_step.${this.addressId}.concierge_call_scheduled`,
          value: 'true'
        });
      } catch {
        // Non-fatal
      }
    }
  }

  private watchForIframe() {
    const container = this.calendlyContainer?.nativeElement;
    if (!container) {return;}

    this.observer = new MutationObserver(() => {
      const iframe = container.querySelector('iframe') as HTMLIFrameElement;
      if (iframe) {
        iframe.setAttribute('scrolling', 'no');
        iframe.style.height = '2000px';
        this.observer?.disconnect();
      }
    });

    this.observer.observe(container, { childList: true, subtree: true });
  }

  ngOnDestroy() {
    this.observer?.disconnect();
    window.removeEventListener('message', this.boundCalendlyListener);
    if (this.calendlyScript) {
      document.head.removeChild(this.calendlyScript);
      this.calendlyScript = null;
    }
  }
}
